import { type FC, useMemo } from 'react';
import type { CallToActionProps } from './types';

import styled, { css } from 'styled-components';
import {
  filterBackgroundColor,
  sanitizeRichText,
  COLOR_BLUE_3,
  defaultWebValue,
  generateAccessKey
} from 'shared/helpers/index';
import { useSales, useWebsiteEditor } from 'shared/hooks/index';
import { Wrapper, Button, Download } from '../Common';
import useIsEmpty from 'shared/hooks/useIsEmpty';
import { FlexBox } from 'shared/components/Common';

const Container = styled.div<{ backgroundColor: string }>`
  padding: 110px 0;
  background: ${(props) => props.backgroundColor};

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
    padding: 80px 0;
    `}

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    padding: 60px 0;
    `}
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Title = styled.h2<{ isEmpty?: boolean; editable?: boolean }>`
  font-size: ${({ theme: { $headlineFont } }) => $headlineFont};
  font-weight: 700;
  width: 100%;
  line-height: 1.35em;
  margin: 0 auto;
  text-align: center;
  margin-bottom: 48px;

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
    margin-bottom: 38px;
    `}

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    width: 100%;
    margin-bottom: 26px;
    `}

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    margin-bottom: 36px;
    `}

  ${({ isEmpty, editable }) =>
    isEmpty
      ? `
    display: ${editable ? 'block' : 'none'};
    &:not(:focus):before {
      content: attr(data-placeholder);
      cursor: text;
      color: #aaa;
      position: absolute;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  `
      : `
    &:before {
      content: none;
    } 
  `}
`;

const Benefits = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  margin: 0 auto;
  margin-bottom: 64px;

  ${({ theme: { $width } }) =>
    $width <= 1254 &&
    `
    width: 100%;
    `}

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
    margin-bottom: 34px;
    `}

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    flex-direction: column;
    align-items: center;
    gap: 20px;
    `}
`;

const Item = styled.div`
  font-size: ${({ theme: { $subheadlineFont } }) => $subheadlineFont};
  display: flex;
  align-items: center;
  width: 29%;

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    width: auto;

    &:not(:last-child) {
      margin-bottom: 20px;
    }
    `}
`;

const Check = styled.div`
  width: 24px;
  height: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
  background: #fff;
  border-radius: 6px;
  margin-right: 17px;
  flex-shrink: 0;

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
    margin-right: 12px;
    `}
`;

const Icon = styled.svg`
  width: 14px;
  height: 11px;
`;

const Text = styled.p<{ isEmpty?: boolean; editable?: boolean }>`
  width: 93%;
  font-size: ${({ theme: { $subheadlineFont } }) => $subheadlineFont};
  ${({ isEmpty, editable }) =>
    isEmpty
      ? `
    display: ${editable ? 'block' : 'none'};
    &:not(:focus):before {
      content: attr(data-placeholder);
      cursor: text;
      color: #aaa;
      position: absolute;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  `
      : `
    &:before {
      content: none;
    } 
  `}
`;

const FlexBoxStyled = styled(FlexBox)`
  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    justify-content: center;
    width: 100%;
  `}
`;

const buttonStyles = css`
  max-width: 415px;
  display: inline-block;

  ${({ theme: { $width } }) =>
    $width <= 1254 &&
    `
      max-width: 345px;
    `}

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
      max-width: 380px;
      padding-top: 24px;
      padding-bottom: 26px;
      display: block;
      margin: 0 auto;
    `}
`;

const DownloadStyled = styled(Download)`
  ${buttonStyles}
`;

const StyledButton = styled(Button)`
  ${buttonStyles}
`;

export const V2: FC<CallToActionProps> = (props) => {
  const {
    button = defaultWebValue,
    title = defaultWebValue,
    backgroundColor = COLOR_BLUE_3,
    textColor,
    benefits = [],
    meta,
    hidden,
    customBackgroundColor,
    order
  } = props;

  const {
    updateActiveElement,
    resetActiveElement,
    containerClassname,
    editable,
    websiteAssembler
  } = useWebsiteEditor();

  const { purchaseElementId, type, isDashboard } = useSales();

  const { isEmpty: isTitleEmpty } = useIsEmpty(meta, 'title');
  const { checkBenefits } = useIsEmpty(meta, 'content', true);

  // Accessibility improvements
  const finalBackgroundColor = useMemo(
    () => customBackgroundColor || backgroundColor,
    [customBackgroundColor, backgroundColor]
  );

  const finalTextColor = useMemo(
    () => filterBackgroundColor(finalBackgroundColor, textColor),
    [finalBackgroundColor, textColor]
  );

  const titleAccessKey = generateAccessKey('t', order);
  const skipLinkId = `skip-to-cta-v2-${meta['accessorKey'] || 'call-to-action'}`;

  const handleModalError = (error: Error | unknown) => {
    // Announce error to screen readers
    const errorAnnouncement = document.createElement('div');
    errorAnnouncement.setAttribute('aria-live', 'polite');
    errorAnnouncement.setAttribute('aria-atomic', 'true');
    errorAnnouncement.className = 'sr-only';
    errorAnnouncement.textContent = `Action error: ${error instanceof Error ? error.message : 'Action failed'}`;
    document.body.appendChild(errorAnnouncement);
    setTimeout(() => document.body.removeChild(errorAnnouncement), 1000);
  };

  return (
    <Container
      backgroundColor={finalBackgroundColor}
      className={containerClassname}
      aria-label="Call to action section"
      role="region"
      style={{
        display: hidden && !websiteAssembler ? 'none' : 'block',
        opacity: hidden ? 0.3 : 1
      }}>
      {/* Skip link for keyboard navigation */}
      <span className="sr-only">
        <a href={`#${skipLinkId}`}>Skip to call to action content</a>
      </span>

      <Wrapper id={skipLinkId}>
        <Benefits role="list" aria-label="Additional list">
          {benefits.map(({ content }, index) => {
            const isContentEmpty = checkBenefits(index);
            const additionalAccessKey = generateAccessKey('b', index);

            return (
              <Item
                key={`calltoaction-${index}`}
                role="listitem"
                aria-label={`Additional ${index + 1}`}>
                <Check role="img" aria-label="Checkmark" aria-hidden="false">
                  <Icon
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 11 9"
                    fill="none"
                    aria-hidden="true"
                    focusable="false">
                    <path
                      d="M3.86159 6.74161L1.58705 4.46707L0.8125 5.23616L3.86159 8.28525L10.407 1.73979L9.63795 0.970703L3.86159 6.74161Z"
                      fill="black"
                    />
                  </Icon>
                </Check>

                <Text
                  id={content?.id}
                  dangerouslySetInnerHTML={{
                    __html: sanitizeRichText(content?.val, editable)
                  }}
                  style={{ color: finalTextColor }}
                  isEmpty={isContentEmpty}
                  {...(isDashboard
                    ? {
                        onClick: updateActiveElement,
                        onBlur: resetActiveElement,
                        'data-accessor-parent': meta['accessorKey'],
                        'data-accessorkey': 'content',
                        'data-placeholder': '+',
                        editable: true
                      }
                    : {
                        role: 'textbox',
                        'aria-multiline': 'true',
                        'aria-label': `Additional ${index + 1} description`,
                        accessKey: additionalAccessKey,
                        title: `Press Alt+${additionalAccessKey} to focus this additional`,
                        tabIndex: 0
                      })}
                />
              </Item>
            );
          })}
        </Benefits>

        <TitleWrapper>
          <Title
            id={title.id}
            dangerouslySetInnerHTML={{ __html: sanitizeRichText(title?.val, editable) }}
            style={{ color: finalTextColor }}
            isEmpty={isTitleEmpty}
            {...(isDashboard
              ? {
                  onClick: updateActiveElement,
                  onBlur: resetActiveElement,
                  'data-accessor-parent': meta['accessorKey'],
                  'data-accessorkey': 'title',
                  'data-benefits': true,
                  'data-placeholder': '+',
                  editable: true
                }
              : {
                  'aria-label':
                    isTitleEmpty || !title.val?.trim() ? 'Call to action title' : undefined,
                  accessKey: titleAccessKey,
                  title: `Press Alt+${titleAccessKey} to focus this title`,
                  tabIndex: 0
                })}
          />

          <FlexBoxStyled position="relative" role="group" aria-label="Action button group">
            {meta['url'] || type === 'delivery' ? (
              <DownloadStyled
                textColor={textColor}
                button={button}
                meta={meta}
                outerOnClick={() => {
                  try {
                    // empty needs only for error
                  } catch (error) {
                    handleModalError(error);
                  }
                }}
                aria-label={button.val ? `Download: ${button.val}` : 'Download button'}
              />
            ) : (
              <StyledButton
                to={purchaseElementId}
                button={button}
                meta={meta}
                outerOnClick={() => {
                  try {
                    // empty needs only for error
                  } catch (error) {
                    handleModalError(error);
                  }
                }}
                aria-label={button.val ? `Action: ${button.val}` : 'Action button'}
              />
            )}
          </FlexBoxStyled>
        </TitleWrapper>
      </Wrapper>
    </Container>
  );
};

V2.displayName = 'CallToActionVersion2';
